/**
 * Remote players — meshes, name plates, and pose from the snapshot buffer.
 *
 * ── The v1 bug this file fixes ────────────────────────────────────────────
 *
 * `RemotePlayers.js:117` did `new THREE.Color().setHSL(Math.random(), 0.7, 0.5)`
 * per remote player. Random, unsynchronised across clients, and re-rolled on
 * reconnect: the same opponent was a different colour on every screen and a
 * different colour again after a dropped socket. With a five-character roster
 * where colour IS the identity — you decide whether to commit based on whether
 * that silhouette is a 150-HP Monolith or an 80-HP Volt — that is fatal.
 *
 * Colour is now `CHARACTERS[characterId].colorHex`. A pure function of the
 * character, identical on every client, stable across reconnects.
 *
 * ── The second change ─────────────────────────────────────────────────────
 *
 * v1 advanced remote players by their last velocity and lerped toward the last
 * known position (`:192-213`). That is extrapolation: it guesses, and it guesses
 * wrong exactly when someone stops or dies. Poses now come from
 * `SnapshotBuffer`, which interpolates between two RECEIVED snapshots 100ms in
 * the past and never invents a frame.
 */

import * as THREE from 'three/webgpu';
import { DECOY_ID_PREFIX } from '@chaos/sim';
import { playDecoyPop, playBlinkIn, playSpawnIn } from '../combat/AbilityVFX.js';

/** Ids that left the snapshot recently, for the blink-in on their return. */
const _recentlyGone = new Map();
/** A body back within this long of leaving was faded, not gone. */
const BLINK_IN_WINDOW_MS = 3000;
import { remotePlayers, session, localPlayer } from '../state.js';
import { createCharacterModel, updateDashTrail, setCharacterShimmer } from '../characters/CharacterModels.js';
import { createCharacterBatcher } from '../characters/proc/CharacterBatcher.js';
import { updateSquashStretch } from '../PlayerVisuals.js';
import { removeHullOutline } from '../CheapOutline.js';
import { setStatusAura, disposeStatusAura } from '../combat/StatusAura.js';
import { shortAngleDist } from '../math.js';
import { outlineMode, onOutlineModeChange } from '../PostProcessing.js';
import { playJumpSound, playLandSound } from '../audio/SoundManager.js';
import { useSkinned } from '../config.js';
import { preloadSkinned, hasSkinned, createSkinnedModel } from '../characters/SkinnedCharacter.js';
import { watchVisual, upgradeVisual } from '../assets/VisualUpgrade.js';
import { registerDiagnostics } from '../performance/runtime.js';

/** Same audibility gate as ability casts in main.js: beyond this, not a threat. */
const SOUND_RANGE = 28;

/**
 * How long a defeated body's scattered blocks stay on the deck. The pieces
 * settle inside ~1s; the rest is the "eliminated, pot settled" read. After
 * that the pile hides — eight piles by the endgame is clutter, and the kill
 * feed owns the record by then.
 */
const SCATTER_SHOW_MS = 3200;

let _scene = null;
/** Per-part instanced pools — ALL remote bodies land in these few draws. */
let _batcher = null;

export function initRemotePlayers(sceneRef) {
  _scene = sceneRef;
  _batcher = createCharacterBatcher(sceneRef);
  registerDiagnostics('characters', characterBatcherStats);
  // Hull outlines belong to the pools only on the tiers where meshes own the
  // silhouette; the pipeline tiers outline the instances in post.
  _batcher.setHullsVisible(outlineMode() === 'hull');
  onOutlineModeChange((mode) => _batcher.setHullsVisible(mode === 'hull'));
}

/** Exposed for measurement (draw calls are the budget this design is held to). */
export function characterBatcherStats() {
  return _batcher?.stats() ?? null;
}

function createNamePlate(name, colorHex) {
  // 4x the old resolution: the 256px canvas came out as a blurry slab. The pill
  // is drawn no wider than the text needs, so short names get short plates
  // instead of one fixed block that walls off the fight behind it.
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 112;
  const ctx = canvas.getContext('2d');
  const text = (name || 'Player').slice(0, 16);
  ctx.font = "700 52px Fredoka, Nunito, system-ui, sans-serif";
  const w = Math.min(492, ctx.measureText(text).width + 64);
  const x = (512 - w) / 2;

  // OPAQUE ink, with the character's colour as a thick edge. The plate used to
  // be 62% black with a 4px rim — over a bright deck the fill washed out and the
  // white text lost its ground. The HUD solved the same problem the same way:
  // an outline is what lets white sit on anything.
  ctx.beginPath();
  ctx.roundRect(x, 20, w, 72, 36);
  ctx.fillStyle = '#04080a';
  ctx.fill();
  // Character-coloured edge: the plate identifies before the text resolves.
  ctx.strokeStyle = `#${colorHex.toString(16).padStart(6, '0')}`;
  ctx.lineWidth = 7;
  ctx.stroke();

  // Stroke the glyphs too, matching `paint-order: stroke fill` in the HUD, so a
  // name over a pale wall keeps its edges.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 8;
  ctx.strokeStyle = '#04080a';
  ctx.strokeText(text, 256, 58);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, 256, 58);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteNodeMaterial({ map: texture, transparent: true });
  material.depthTest = false;
  material.depthWrite = false;
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(1.85, 0.4, 1);
  // ABOVE the health bar, not below it. The bar sits at 2.25 and the plate was
  // at 1.92, so the two overlapped into one unreadable smudge at any distance —
  // visible the moment both were given real outlines and stopped being faint
  // enough to blend. Name on top, health under it, body under that: the order
  // reads down the screen in the order you need it.
  //
  // The clearance has to cover the bar at its BIGGEST. It scales up to 1.9x with
  // distance about its own origin, so its top reaches 2.25 + 0.11*1.9 = 2.46;
  // this plate's half-height is 0.2. 2.66 left them exactly touching at range,
  // which is the same collision again with a smaller gap.
  sprite.position.y = 2.95;
  sprite.renderOrder = 19;
  sprite.userData.isNamePlate = true;
  sprite.userData.noOutline = true;
  return sprite;
}

function createRemoteMesh(id, pose, displayName) {
  // The seed is the player id — appearance is a pure function of it, so this
  // body is identical on every screen and IS this player's body next match
  // too. Decoys are the one exception: their wire id (`decoy#N`) deliberately
  // carries no owner, so they seed from the characterId — every decoy is the
  // canonical body of its family. (Matching the owner's variant would need
  // the owner id on the decoy row: a wire change, flagged, not smuggled.)
  const seed = id.startsWith(DECOY_ID_PREFIX) ? pose.characterId : id;
  const group = watchVisual(createCharacterModel(pose.characterId, seed, { pooled: true }));
  if (useSkinned) void preloadSkinned([pose.characterId]);
  _batcher.add(group);
  group.add(createNamePlate(displayName, group.userData.colorHex));
  group.position.set(pose.x, pose.y, pose.z);
  group.rotation.y = pose.yaw + Math.PI;
  group.userData.lastY = pose.y;
  group.userData.displayName = displayName;
  return group;
}

/**
 * Applies one sampled frame from the snapshot buffer.
 *
 * `poses` is `Map<id, pose>` and is authoritative about who exists: an id that
 * is absent has either left or gone invisible, and in both cases the server
 * decided it, not this function.
 */
export function applyPoses(poses, delta, now, nameFor, bodyScale = 1) {
  for (const [id, pose] of poses) {
    if (id === session.userId) continue;

    let mesh = remotePlayers.get(id);
    if (!mesh) {
      mesh = createRemoteMesh(id, pose, nameFor(id));
      remotePlayers.set(id, mesh);
      _scene.add(mesh);
      // A body that was here a moment ago and is back (a faded trickster
      // reappearing), or a decoy stepping out of its original, BLINKS in.
      // A fresh join does not: nothing was there to blink from.
      const gone = _recentlyGone.get(id);
      if ((gone && now - gone < BLINK_IN_WINDOW_MS) || id.startsWith(DECOY_ID_PREFIX)) {
        playBlinkIn(mesh.position, pose.characterId);
      }
      _recentlyGone.delete(id);
    }

    // Revive first so a deferred upgrade can take the new body. Death poses
    // never replace a driver, including on the first observed dead frame.
    if (pose.phase !== 'dead' && mesh.userData.driver?.dead) {
      mesh.userData.driver.revive();
      // Back in the fight: the beam-in, on their body, where it stands now.
      playSpawnIn({ x: pose.x, y: pose.y, z: pose.z }, pose.characterId);
    }
    if (useSkinned && pose.phase !== 'dead' && hasSkinned(pose.characterId)) {
      upgradeVisual(mesh, () => createSkinnedModel(pose.characterId, { pooled: true }), {
        before: root => _batcher?.remove(root),
        dispose: disposeBodyMaterials,
      });
    }
    const driver = mesh.userData.driver;

    // Death is an EDGE, not a state to hide: on alive→dead the toy falls
    // apart (the scatter is seeded, so every screen sees the same pile), the
    // pile lingers SCATTER_SHOW_MS, then hides. The gym and scrims respawn —
    // dead→alive puts the toy back together from the recorded transforms.
    if (pose.phase === 'dead') {
      if (!driver.dead) {
        driver.die();
        mesh.userData.diedAt = now;
      }
      mesh.visible = now - (mesh.userData.diedAt ?? now) < SCATTER_SHOW_MS;
      driver.update(now, delta, {});
      continue;
    }
    if (driver.dead) driver.revive();
    mesh.visible = true;

    // A pose that stopped arriving holds its last position rather than gliding.
    if (!pose.extrapolated) {
      mesh.position.set(pose.x, pose.y, pose.z);
    }

    // Facing is snapshot yaw, damped just enough to hide the 15Hz step. Turning
    // toward the velocity — v1's approach — makes a strafing opponent face the
    // wrong way, and facing is what tells you which way an ability will land.
    mesh.rotation.y += shortAngleDist(mesh.rotation.y, pose.yaw + Math.PI) * Math.min(1, delta * 18);

    const vy = (pose.y - (mesh.userData.lastY ?? pose.y)) / Math.max(delta, 1e-4);
    mesh.userData.lastY = pose.y;
    updateSquashStretch(mesh, vy, bodyScale);

    // Jump/land sounds off the pose's vertical velocity, at reduced gain and only
    // within earshot — the same gate cast audio uses. Skipped when delta is the
    // hitstop epsilon: dividing by 1e-4 turns a still frame into a fake launch.
    if (delta > 1e-3) {
      const wasVy = mesh.userData.vyPrev ?? 0;
      mesh.userData.vyPrev = vy;
      const near = mesh.position.distanceTo(localPlayer.renderPos) < SOUND_RANGE;
      // A fast fall is LATCHED rather than edge-matched: snapshot interpolation
      // smears a landing into several decelerating frames, so the frame that
      // reads grounded never sees the pre-impact velocity directly.
      if (vy < -12) mesh.userData.fallPeak = Math.min(mesh.userData.fallPeak ?? 0, vy);
      // vy < 40 rejects teleport spikes — no real jump leaves the ground at 40u/s.
      if (near && vy > 8 && vy < 40 && wasVy <= 2) playJumpSound(0.05);
      if (vy >= -1) {
        const peak = mesh.userData.fallPeak ?? 0;
        if (peak < -12 && near) playLandSound(Math.min(1, -peak / 40), 0.35);
        mesh.userData.fallPeak = 0;
      }
    }

    // The pose driver owns every joint: run cycle from the snapshot's own
    // velocity, aim from the (already damped) visual yaw, so the legs face
    // the travel while the torso faces the threat — the twin-stick split.
    driver.update(now, delta, { vx: pose.vx, vz: pose.vz, aimYaw: mesh.rotation.y });
    updateDashTrail(mesh, now);

    const effects = pose.effects ?? [];
    // Shimmer is a no-op on a pooled body (per-instance opacity is the one
    // thing shared opaque pools cannot do) — and by design it never fires:
    // the server omits invisible opponents from snapshots entirely, so only
    // your OWN faded body ever needs the fade, and that one is un-pooled.
    setCharacterShimmer(mesh, effects.includes('invisible') ? 0.72 : 0);
    // Everything else the snapshot says is true about this body. Until now the
    // only effect drawn was the one above, so an opponent's stun, slow, shield
    // or buff was information the server sent and the client threw away.
    setStatusAura(mesh, effects, mesh.userData.colorHex);
  }

  // Anything the server stopped sending is gone from our view. Removing on the
  // sample rather than on a message means an opponent who turns invisible
  // disappears for the same reason and by the same path as one who leaves.
  for (const [id, mesh] of remotePlayers) {
    if (poses.has(id)) continue;
    if (id.startsWith(DECOY_ID_PREFIX)) {
      // A decoy that is gone was shot or ran out: it POPS where it stood.
      playDecoyPop(mesh.position, mesh.userData.characterId ?? 'mirage');
    } else {
      _recentlyGone.set(id, now);
    }
    removeRemotePlayer(id, mesh);
  }

  // Poses are final for the frame — flatten every pooled body into the
  // per-part instance buffers in one pass.
  _batcher?.update();
}

/**
 * Release what a body owns, and nothing it merely borrows.
 *
 * The distinction only appeared with skinned characters. A procedural body
 * builds its own materials and canvas textures and really does leak them, which
 * is what this used to assume of everything. A skinned body's meshes are marked
 * `fromAsset`: their material is a per-instance clone, so it is ours to dispose,
 * but the texture behind it belongs to the preloaded GLB that every other body
 * of that character is still drawn with — disposing that when one player leaves
 * blanks the rest of them.
 *
 * Everything not from the asset — the nameplate sprite, the leader crown — is
 * per-instance on both kinds of body and still has to go.
 */
export function disposeBodyMaterials(root) {
  root.traverse?.((child) => {
    if (!child.isMesh && !child.isSprite && !child.isSkinnedMesh) return;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    for (const mat of mats) {
      if (!mat) continue;
      if (!child.userData?.fromAsset) mat.map?.dispose?.();
      mat.dispose?.();
    }
  });
}

export function removeRemotePlayer(id, meshRef) {
  const mesh = meshRef ?? remotePlayers.get(id);
  if (!mesh) return;
  _scene.remove(mesh);
  _batcher?.remove(mesh);
  removeHullOutline(mesh);
  disposeStatusAura(mesh);
  mesh.userData?.driver?.dispose?.();
  disposeBodyMaterials(mesh);
  remotePlayers.delete(id);
}

export function clearRemotePlayers() {
  for (const id of [...remotePlayers.keys()]) removeRemotePlayer(id);
}

/** World position of a remote body, for VFX anchoring. Null if not drawn. */
export function remotePosition(id, out) {
  const mesh = remotePlayers.get(id);
  if (!mesh) return null;
  return out.copy(mesh.position);
}
